/**
 * Implementation Templates admin page (SmartImprove) — Feb 2026.
 *
 * Consulting-grade warehouse improvement execution templates used for
 * rollout planning, transformation sequencing, resource estimation, and
 * operational enhancement programs.
 *
 * Position: SmartImprove stays LIGHTER than SmartEvaluate's full SAP
 * implementation planner. These templates capture reusable warehouse
 * transformation execution programs without ERP-implementation overhead.
 *
 * The backend collection (`smartimprove_implementation_templates`) and
 * route slug (`/implementation-templates`) are preserved for backward
 * compatibility with the deterministic implementation engine.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import { Plus, Pencil, Trash2, Layers, Users, ListChecks, ChevronDown, ChevronRight } from "lucide-react";
import { implementationTemplatesApi } from "../../lib/smartimproveAdminApi";

// ----- Canonical option sets -----
const ROLLOUT_STRATEGIES = [
  { value: "quick_win", label: "Quick Win" },
  { value: "pilot", label: "Pilot + Rollout" },
  { value: "rollout", label: "Multi-Site Harmonization" },
  { value: "phased", label: "Phased Optimization" },
  { value: "continuous", label: "Continuous Improvement" },
];

const ROLLOUT_MODELS = [
  { value: "", label: "—" },
  { value: "quick_win", label: "Quick Win" },
  { value: "pilot_rollout", label: "Pilot + Rollout" },
  { value: "big_bang", label: "Big Bang Improvement" },
  { value: "phased", label: "Phased Optimization" },
  { value: "continuous", label: "Continuous Improvement" },
  { value: "multi_site", label: "Multi-Site Harmonization" },
];

const TRANSFORMATION_THEMES = [
  "RF Modernization",
  "Warehouse Standardization",
  "Process Harmonization",
  "Inventory Optimization",
  "Workforce Productivity",
  "Automation Enablement",
  "KPI & Visibility Improvements",
  "Digital Transformation",
  "Operational Stability",
  "Integration Reliability",
  "Scalability Readiness",
];

const COMPLEXITY = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
];

const CHANGE_MANAGEMENT = [
  { value: "", label: "—" },
  { value: "none", label: "None" },
  { value: "light", label: "Light" },
  { value: "standard", label: "Standard" },
  { value: "intensive", label: "Intensive" },
];

const DISRUPTION = [
  { value: "", label: "—" },
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
];

const ROLLOUT_GROUP_OPTIONS = [
  "Stabilization",
  "Process Optimization",
  "Standardization",
  "Automation Enablement",
  "Governance",
];

const PHASE_OPTIONS = [
  "Discovery",
  "Solution Design",
  "Build / Enhancement",
  "SIT",
  "UAT",
  "Hypercare",
];

const RESOURCE_ROLES = [
  "SAP EWM Consultant",
  "Solution Architect",
  "RF Mobility Specialist",
  "Integration Consultant",
  "Warehouse Operations Lead",
  "Change Lead",
  "Change Management Lead",
  "Training Lead",
  "QA / Validation Lead",
  "Site Deployment Coordinator",
];

const STRATEGY_LABEL = {
  quick_win: "Quick Win",
  pilot: "Pilot + Rollout",
  rollout: "Multi-Site Harmonization",
  phased: "Phased Optimization",
  continuous: "Continuous Improvement",
};

const COMPLEXITY_PILL = {
  low: "bg-[#F1F5F9] text-[#475569] border-[#E2E8F0]",
  medium: "bg-[#EFF6FF] text-[#1D4ED8] border-[#BFDBFE]",
  high: "bg-[#FFF7ED] text-[#C2410C] border-[#FED7AA]",
};

const STRATEGY_PILL = {
  quick_win: "bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]",
  pilot: "bg-[#EFF6FF] text-[#1D4ED8] border-[#BFDBFE]",
  rollout: "bg-[#F0F9FF] text-[#075985] border-[#BAE6FD]",
  phased: "bg-[#FEF3C7] text-[#92400E] border-[#FCD34D]",
  continuous: "bg-[#F3F4F6] text-[#374151] border-[#D1D5DB]",
};

function slugify(name) {
  return String(name || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 80);
}

const EMPTY = {
  id: null,
  slug: "",
  title: "",
  description: "",
  rollout_strategy: "quick_win",
  rollout_model: "",
  transformation_theme: "",
  complexity: "medium",
  recommended_use_case: "",
  duration_min_weeks: "",
  duration_max_weeks: "",
  estimated_duration_weeks: "",
  change_management_level: "",
  business_disruption_level: "",
  recommended_for_multi_site: false,
  governance_required: false,
  implementation_notes: "",
  rollout_groups: [],
  phases_detail: [],
  resources: [],
  weekly_effort_matrix: [],
  active: true,
  sort_order: 0,
};

function ChipMultiSelect({ value = [], options, onChange, testId }) {
  const set = new Set(value || []);
  const toggle = (opt) => {
    const next = new Set(set);
    if (next.has(opt)) next.delete(opt);
    else next.add(opt);
    onChange(Array.from(next));
  };
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testId}>
      {options.map((opt) => {
        const active = set.has(opt);
        return (
          <button
            key={opt}
            type="button"
            onClick={() => toggle(opt)}
            className={`px-2 py-1 text-[11px] rounded-sm border transition ${
              active
                ? "bg-[#025979] text-white border-[#025979]"
                : "bg-white text-[#374151] border-[#E5E7EB] hover:border-[#94A3B8]"
            }`}
            data-testid={`${testId}-${slugify(opt)}`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function PhasesEditor({ value = [], onChange }) {
  const addRow = (name = "") =>
    onChange([...(value || []), { name, duration_weeks: "" }]);
  const updateRow = (i, patch) => {
    const next = [...value];
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  const removeRow = (i) => onChange(value.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-2" data-testid="phases-editor">
      {value.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <Select
            value={p.name || ""}
            onValueChange={(v) => updateRow(i, { name: v })}
          >
            <SelectTrigger className="rounded-sm text-xs flex-1">
              <SelectValue placeholder="Phase" />
            </SelectTrigger>
            <SelectContent>
              {PHASE_OPTIONS.map((opt) => (
                <SelectItem key={opt} value={opt}>
                  {opt}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            type="number"
            step="0.5"
            value={p.duration_weeks ?? ""}
            onChange={(e) => updateRow(i, { duration_weeks: e.target.value })}
            placeholder="weeks"
            className="rounded-sm text-xs w-24"
          />
          <button
            onClick={() => removeRow(i)}
            type="button"
            className="text-[#B91C1C] hover:text-[#7F1D1D]"
            aria-label="Remove phase"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => addRow()}
        className="text-xs font-semibold text-[#025979] hover:text-[#03738C]"
      >
        + Add Phase
      </button>
    </div>
  );
}

function ResourcesEditor({ value = [], onChange }) {
  const addRow = () =>
    onChange([
      ...(value || []),
      {
        role: "",
        typical_hours: "",
        onsite_rate_usd_per_hr: "",
        offshore_rate_usd_per_hr: "",
        notes: "",
      },
    ]);
  const updateRow = (i, patch) => {
    const next = [...value];
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  const removeRow = (i) => onChange(value.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-2" data-testid="resources-editor">
      {value.length > 0 ? (
        <div className="grid grid-cols-12 gap-2 px-1 text-[10px] text-[#6B7280] uppercase tracking-wider">
          <div className="col-span-4">Role</div>
          <div className="col-span-2">Typical Hours</div>
          <div className="col-span-2">Onsite $/hr</div>
          <div className="col-span-2">Offshore $/hr</div>
          <div className="col-span-1">Notes</div>
          <div className="col-span-1" />
        </div>
      ) : null}
      {value.map((r, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 items-center">
          <Select
            value={r.role || ""}
            onValueChange={(v) => updateRow(i, { role: v })}
          >
            <SelectTrigger className="rounded-sm text-xs col-span-4">
              <SelectValue placeholder="Role" />
            </SelectTrigger>
            <SelectContent>
              {RESOURCE_ROLES.map((opt) => (
                <SelectItem key={opt} value={opt}>
                  {opt}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            type="number"
            value={r.typical_hours ?? ""}
            onChange={(e) => updateRow(i, { typical_hours: e.target.value })}
            placeholder="hrs"
            className="rounded-sm text-xs col-span-2"
          />
          <Input
            type="number"
            value={r.onsite_rate_usd_per_hr ?? ""}
            onChange={(e) =>
              updateRow(i, { onsite_rate_usd_per_hr: e.target.value })
            }
            placeholder="onsite"
            className="rounded-sm text-xs col-span-2"
          />
          <Input
            type="number"
            value={r.offshore_rate_usd_per_hr ?? ""}
            onChange={(e) =>
              updateRow(i, { offshore_rate_usd_per_hr: e.target.value })
            }
            placeholder="offshore"
            className="rounded-sm text-xs col-span-2"
          />
          <Input
            value={r.notes ?? ""}
            onChange={(e) => updateRow(i, { notes: e.target.value })}
            placeholder="Notes"
            className="rounded-sm text-xs col-span-1"
          />
          <button
            onClick={() => removeRow(i)}
            type="button"
            className="text-[#B91C1C] hover:text-[#7F1D1D] col-span-1 justify-self-end"
            aria-label="Remove resource"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={addRow}
        className="text-xs font-semibold text-[#025979] hover:text-[#03738C]"
      >
        + Add Resource
      </button>
      <p className="text-[11px] text-[#6B7280] mt-2">
        Rates stored in USD/hour. Currency conversion handled by the
        consultant dashboard, mirroring SmartEvaluate.
      </p>
    </div>
  );
}

function Section({ icon: Icon, title, subtitle, defaultOpen = true, testId, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-[#E5E7EB] rounded-sm" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[#F8FAFC]"
      >
        <span className="flex items-center gap-2">
          {Icon ? <Icon className="w-3.5 h-3.5 text-[#025979]" /> : null}
          <span className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#025979]">
            {title}
          </span>
          {subtitle ? (
            <span className="text-[11px] text-[#6B7280] ml-2 normal-case font-normal tracking-normal">
              {subtitle}
            </span>
          ) : null}
        </span>
        {open ? (
          <ChevronDown className="w-3.5 h-3.5 text-[#6B7280]" />
        ) : (
          <ChevronRight className="w-3.5 h-3.5 text-[#6B7280]" />
        )}
      </button>
      {open ? (
        <div className="px-4 pt-1 pb-4 border-t border-[#F1F5F9]">{children}</div>
      ) : null}
    </div>
  );
}

function ImplementationDialog({
  open,
  onOpenChange,
  value,
  onChange,
  onSave,
  saving,
}) {
  const isEdit = !!value?.id;
  const set = (patch) => onChange({ ...value, ...patch });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-5xl"
        data-testid="implementation-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edit Implementation Template" : "New Implementation Template"}
          </DialogTitle>
          <DialogDescription>
            Reusable consulting-grade warehouse improvement execution
            program. Define rollout groups, phases, and the resource model
            once — engagements adopt and adjust as needed.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2 max-h-[72vh] overflow-y-auto pr-1">
          {/* ---------- OVERVIEW ---------- */}
          <Section icon={ListChecks} title="Overview" defaultOpen testId="impl-section-overview">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-xs font-medium text-[#374151] mb-1">
                  Template Name
                </label>
                <Input
                  value={value?.title || ""}
                  onChange={(e) => set({ title: e.target.value })}
                  placeholder="e.g. RF Modernization Program"
                  className="rounded-sm text-xs"
                  data-testid="impl-field-title"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#374151] mb-1">
                  Implementation Strategy
                </label>
                <Select
                  value={value?.rollout_strategy || "quick_win"}
                  onValueChange={(v) => set({ rollout_strategy: v })}
                >
                  <SelectTrigger className="rounded-sm text-xs" data-testid="impl-field-strategy">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLLOUT_STRATEGIES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#374151] mb-1">
                  Rollout Model
                </label>
                <Select
                  value={value?.rollout_model || "__none"}
                  onValueChange={(v) => set({ rollout_model: v === "__none" ? "" : v })}
                >
                  <SelectTrigger className="rounded-sm text-xs" data-testid="impl-field-rollout-model">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLLOUT_MODELS.map((s) => (
                      <SelectItem key={s.value || "__none"} value={s.value || "__none"}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#374151] mb-1">
                  Transformation Theme
                </label>
                <Select
                  value={value?.transformation_theme || ""}
                  onValueChange={(v) => set({ transformation_theme: v })}
                >
                  <SelectTrigger className="rounded-sm text-xs" data-testid="impl-field-theme">
                    <SelectValue placeholder="Select theme" />
                  </SelectTrigger>
                  <SelectContent>
                    {TRANSFORMATION_THEMES.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#374151] mb-1">Complexity</label>
                <Select
                  value={value?.complexity || "medium"}
                  onValueChange={(v) => set({ complexity: v })}
                >
                  <SelectTrigger className="rounded-sm text-xs" data-testid="impl-field-complexity">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COMPLEXITY.map((c) => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-[#374151] mb-1">Description</label>
                <Textarea
                  value={value?.description || ""}
                  onChange={(e) => set({ description: e.target.value })}
                  rows={2}
                  className="rounded-sm text-xs"
                  data-testid="impl-field-description"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-[#374151] mb-1">Recommended Use Case</label>
                <Textarea
                  value={value?.recommended_use_case || ""}
                  onChange={(e) => set({ recommended_use_case: e.target.value })}
                  rows={2}
                  placeholder="When consultants should reach for this template."
                  className="rounded-sm text-xs"
                  data-testid="impl-field-use-case"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#374151] mb-1">Duration Min (weeks)</label>
                <Input
                  type="number"
                  value={value?.duration_min_weeks ?? ""}
                  onChange={(e) => set({ duration_min_weeks: e.target.value })}
                  className="rounded-sm text-xs"
                  data-testid="impl-field-duration-min"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#374151] mb-1">Duration Max (weeks)</label>
                <Input
                  type="number"
                  value={value?.duration_max_weeks ?? ""}
                  onChange={(e) => set({ duration_max_weeks: e.target.value })}
                  className="rounded-sm text-xs"
                  data-testid="impl-field-duration-max"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#374151] mb-1">Estimated Duration (weeks)</label>
                <Input
                  type="number"
                  step="0.5"
                  value={value?.estimated_duration_weeks ?? ""}
                  onChange={(e) => set({ estimated_duration_weeks: e.target.value })}
                  className="rounded-sm text-xs"
                  data-testid="impl-field-duration-est"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#374151] mb-1">Change Management</label>
                <Select
                  value={value?.change_management_level || "__none"}
                  onValueChange={(v) => set({ change_management_level: v === "__none" ? "" : v })}
                >
                  <SelectTrigger className="rounded-sm text-xs" data-testid="impl-field-change-mgmt">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CHANGE_MANAGEMENT.map((c) => (
                      <SelectItem key={c.value || "__none"} value={c.value || "__none"}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#374151] mb-1">Business Disruption</label>
                <Select
                  value={value?.business_disruption_level || "__none"}
                  onValueChange={(v) => set({ business_disruption_level: v === "__none" ? "" : v })}
                >
                  <SelectTrigger className="rounded-sm text-xs" data-testid="impl-field-disruption">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DISRUPTION.map((c) => (
                      <SelectItem key={c.value || "__none"} value={c.value || "__none"}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <input
                  id="impl-multi-site"
                  type="checkbox"
                  checked={!!value?.recommended_for_multi_site}
                  onChange={(e) => set({ recommended_for_multi_site: e.target.checked })}
                  data-testid="impl-field-multi-site"
                />
                <label htmlFor="impl-multi-site" className="text-xs text-[#374151]">
                  Recommended for multi-site
                </label>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <input
                  id="impl-governance"
                  type="checkbox"
                  checked={!!value?.governance_required}
                  onChange={(e) => set({ governance_required: e.target.checked })}
                  data-testid="impl-field-governance"
                />
                <label htmlFor="impl-governance" className="text-xs text-[#374151]">
                  Governance required
                </label>
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-[#374151] mb-1">Implementation Notes</label>
                <Textarea
                  value={value?.implementation_notes || ""}
                  onChange={(e) => set({ implementation_notes: e.target.value })}
                  rows={2}
                  placeholder="Consulting tips for delivery teams adopting this template."
                  className="rounded-sm text-xs"
                  data-testid="impl-field-notes"
                />
              </div>
            </div>
          </Section>

          {/* ---------- ROLLOUT GROUPS ---------- */}
          <Section icon={Layers} title="Rollout Groups" testId="impl-section-rollout-groups">
            <ChipMultiSelect
              value={value?.rollout_groups || []}
              options={ROLLOUT_GROUP_OPTIONS}
              onChange={(v) => set({ rollout_groups: v })}
              testId="impl-field-rollout-groups"
            />
          </Section>

          {/* ---------- PHASES ---------- */}
          <Section
            icon={ListChecks}
            title="Phases"
            subtitle="High-level execution phases (max 6)"
            testId="impl-section-phases"
          >
            <PhasesEditor
              value={value?.phases_detail || []}
              onChange={(v) => set({ phases_detail: v })}
            />
          </Section>

          {/* ---------- RESOURCES ---------- */}
          <Section
            icon={Users}
            title="Resources"
            subtitle="Core warehouse transformation roles"
            testId="impl-section-resources"
          >
            <ResourcesEditor
              value={value?.resources || []}
              onChange={(v) => set({ resources: v })}
            />
          </Section>

          {/* ---------- META ---------- */}
          <Section title="Meta" defaultOpen={false} testId="impl-section-meta">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <input
                  id="impl-active"
                  type="checkbox"
                  checked={value?.active !== false}
                  onChange={(e) => set({ active: e.target.checked })}
                  data-testid="impl-field-active"
                />
                <label htmlFor="impl-active" className="text-xs text-[#374151]">Active</label>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs text-[#374151]">Sort order</label>
                <Input
                  type="number"
                  value={value?.sort_order ?? 0}
                  onChange={(e) => set({ sort_order: e.target.value })}
                  className="rounded-sm text-xs w-24"
                  data-testid="impl-field-sort-order"
                />
              </div>
            </div>
          </Section>
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="rounded-sm text-xs"
            data-testid="impl-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={onSave}
            disabled={
              saving || !value?.title?.trim() || !value?.rollout_strategy
            }
            className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs"
            data-testid="impl-save"
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TemplateCard({ row, onEdit, onRemove }) {
  const strategy = String(row.rollout_strategy || "").toLowerCase();
  const complexity = String(row.complexity || "").toLowerCase();
  const phases = row.phases_detail || [];
  const resources = row.resources || [];
  const rolloutGroups = row.rollout_groups || [];
  const duration =
    row.estimated_duration_weeks ||
    (row.duration_min_weeks && row.duration_max_weeks
      ? `${row.duration_min_weeks}–${row.duration_max_weeks}`
      : row.duration_min_weeks || row.duration_max_weeks);
  return (
    <div
      className={`bg-white border border-[#E5E7EB] rounded-sm p-5 hover:border-[#94A3B8] transition ${
        row.active === false ? "opacity-60" : ""
      }`}
      data-testid={`impl-card-${row.id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            {strategy ? (
              <span
                className={`inline-flex items-center px-2 py-0.5 text-[10px] font-semibold border rounded-sm ${
                  STRATEGY_PILL[strategy] ||
                  "bg-[#F3F4F6] text-[#374151] border-[#E5E7EB]"
                }`}
              >
                {STRATEGY_LABEL[strategy] || strategy}
              </span>
            ) : null}
            {complexity ? (
              <span
                className={`inline-flex items-center px-2 py-0.5 text-[10px] font-semibold border rounded-sm capitalize ${
                  COMPLEXITY_PILL[complexity] ||
                  "bg-[#F3F4F6] text-[#374151] border-[#E5E7EB]"
                }`}
              >
                {complexity}
              </span>
            ) : null}
            {row.recommended_for_multi_site ? (
              <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold border rounded-sm bg-[#F0F9FF] text-[#075985] border-[#BAE6FD]">
                Multi-site
              </span>
            ) : null}
            {row.active === false ? (
              <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold border rounded-sm bg-[#F3F4F6] text-[#6B7280] border-[#E5E7EB]">
                inactive
              </span>
            ) : null}
          </div>
          <h3
            className="text-base font-bold text-[#111827]"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            {row.title}
          </h3>
          {row.transformation_theme ? (
            <p className="text-[11px] text-[#025979] font-semibold mt-1">
              {row.transformation_theme}
            </p>
          ) : null}
        </div>
        <div className="flex items-center gap-2 whitespace-nowrap">
          <button
            onClick={() => onEdit(row)}
            className="text-xs font-semibold text-[#025979] hover:text-[#03738C]"
            data-testid={`impl-edit-${row.id}`}
            aria-label={`Edit ${row.title}`}
          >
            <Pencil className="inline w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => onRemove(row)}
            className="text-xs font-semibold text-[#B91C1C] hover:text-[#7F1D1D]"
            data-testid={`impl-delete-${row.id}`}
            aria-label={`Delete ${row.title}`}
          >
            <Trash2 className="inline w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      {row.description ? (
        <p className="text-xs text-[#374151] mb-3 line-clamp-2">
          {row.description}
        </p>
      ) : null}
      <div className="grid grid-cols-4 gap-3 text-[11px]">
        <div>
          <p className="text-[#6B7280] uppercase tracking-wider mb-0.5">
            Duration
          </p>
          <p className="text-[#111827] font-semibold">
            {duration ? `${duration} wks` : "—"}
          </p>
        </div>
        <div>
          <p className="text-[#6B7280] uppercase tracking-wider mb-0.5">
            Phases
          </p>
          <p className="text-[#111827] font-semibold">
            {phases.length || "—"}
          </p>
        </div>
        <div>
          <p className="text-[#6B7280] uppercase tracking-wider mb-0.5">
            Resources
          </p>
          <p className="text-[#111827] font-semibold">
            {resources.length || "—"}
          </p>
        </div>
        <div>
          <p className="text-[#6B7280] uppercase tracking-wider mb-0.5">
            Multi-site
          </p>
          <p className="text-[#111827] font-semibold">
            {row.recommended_for_multi_site ? "Yes" : "—"}
          </p>
        </div>
      </div>
      {resources.length > 0 ? (
        <div className="mt-3 pt-3 border-t border-[#F1F5F9]">
          <p className="text-[10px] text-[#6B7280] uppercase tracking-wider mb-1.5">
            Core Roles
          </p>
          <div className="flex flex-wrap gap-1">
            {resources.slice(0, 6).map((r, i) => {
              const meta =
                r.typical_hours && r.onsite_rate_usd_per_hr
                  ? `${r.typical_hours}h · $${r.onsite_rate_usd_per_hr}`
                  : r.typical_hours
                  ? `${r.typical_hours}h`
                  : r.allocation_pct
                  ? `${r.allocation_pct}%`
                  : null;
              return (
                <span
                  key={i}
                  className="px-1.5 py-0.5 text-[10px] rounded-sm border border-[#E5E7EB] bg-white text-[#374151]"
                  title={r.notes || ""}
                >
                  {r.role}
                  {meta ? ` · ${meta}` : ""}
                </span>
              );
            })}
            {resources.length > 6 ? (
              <span className="px-1.5 py-0.5 text-[10px] text-[#6B7280]">
                +{resources.length - 6} more
              </span>
            ) : null}
          </div>
        </div>
      ) : null}
      {rolloutGroups.length > 0 ? (
        <div className="mt-3 pt-3 border-t border-[#F1F5F9]">
          <p className="text-[10px] text-[#6B7280] uppercase tracking-wider mb-1.5">
            Rollout Groups
          </p>
          <div className="flex flex-wrap gap-1">
            {rolloutGroups.map((g) => (
              <span
                key={g}
                className="px-1.5 py-0.5 text-[10px] rounded-sm border border-[#E5E7EB] bg-[#F8FAFC] text-[#374151]"
              >
                {g}
              </span>
            ))}
          </div>
        </div>
      ) : null}
      {row.blended_rate_usd ? (
        <div className="mt-3 pt-3 border-t border-[#F1F5F9] flex items-center justify-between">
          <p className="text-[10px] text-[#6B7280] uppercase tracking-wider">
            Typical Blended Rate
          </p>
          <p className="text-xs text-[#111827] font-semibold">
            ${row.blended_rate_usd} / hr
          </p>
        </div>
      ) : (() => {
        const totalHours = resources.reduce(
          (acc, r) => acc + (Number(r.typical_hours) || 0),
          0
        );
        if (!totalHours) return null;
        const onsite = resources.reduce(
          (acc, r) =>
            acc +
            (Number(r.typical_hours) || 0) *
              (Number(r.onsite_rate_usd_per_hr) || 0),
          0
        );
        const offshore = resources.reduce(
          (acc, r) =>
            acc +
            (Number(r.typical_hours) || 0) *
              (Number(r.offshore_rate_usd_per_hr) || 0),
          0
        );
        // Indicative band: offshore (low) → onsite (high). We pick the
        // non-zero side as the floor so client-side roles with no
        // offshore rate don't crash the band.
        const low = offshore > 0 ? Math.min(offshore, onsite) : onsite;
        const high = Math.max(offshore, onsite);
        const fmt = (n) =>
          n >= 1_000_000
            ? `$${(n / 1_000_000).toFixed(2)}M`
            : n >= 1000
            ? `$${(n / 1000).toFixed(1)}k`
            : `$${Math.round(n)}`;
        return (
          <div className="mt-3 pt-3 border-t border-[#F1F5F9] flex items-center justify-between">
            <p className="text-[10px] text-[#6B7280] uppercase tracking-wider">
              Indicative Effort &amp; Cost
            </p>
            <p className="text-xs text-[#111827] font-semibold">
              {totalHours} hrs · {fmt(low)}–{fmt(high)}
            </p>
          </div>
        );
      })()}
    </div>
  );
}

export default function ImplementationTemplatesPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [filterStrategy, setFilterStrategy] = useState("__all");

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const items = await implementationTemplatesApi.list();
      setRows(items || []);
    } catch (e) {
      toast.error("Failed to load implementation templates");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const sortedRows = useMemo(() => {
    const filtered =
      filterStrategy === "__all"
        ? rows
        : rows.filter((r) => r.rollout_strategy === filterStrategy);
    return [...filtered].sort(
      (a, b) =>
        (a.sort_order ?? 0) - (b.sort_order ?? 0) ||
        String(a.title).localeCompare(String(b.title))
    );
  }, [rows, filterStrategy]);

  const openCreate = () => {
    setForm({ ...EMPTY });
    setDialog(true);
  };
  const openEdit = (row) => {
    setForm({
      id: row.id,
      slug: row.slug || "",
      title: row.title || "",
      description: row.description || "",
      rollout_strategy: row.rollout_strategy || "quick_win",
      rollout_model: row.rollout_model || "",
      transformation_theme: row.transformation_theme || "",
      complexity: row.complexity || "medium",
      recommended_use_case: row.recommended_use_case || "",
      duration_min_weeks: row.duration_min_weeks ?? "",
      duration_max_weeks: row.duration_max_weeks ?? "",
      estimated_duration_weeks: row.estimated_duration_weeks ?? "",
      change_management_level: row.change_management_level || "",
      business_disruption_level: row.business_disruption_level || "",
      recommended_for_multi_site: !!row.recommended_for_multi_site,
      governance_required: !!row.governance_required,
      implementation_notes: row.implementation_notes || "",
      rollout_groups: row.rollout_groups || [],
      phases_detail: row.phases_detail || [],
      resources: row.resources || [],
      weekly_effort_matrix: row.weekly_effort_matrix || [],
      active: row.active !== false,
      sort_order: row.sort_order ?? 0,
    });
    setDialog(true);
  };
  const toNumOrNull = (v) =>
    v === "" || v === null || v === undefined
      ? null
      : Number.isNaN(Number(v))
      ? v
      : Number(v);

  const save = async () => {
    const isEdit = !!form.id;
    const title = (form.title || "").trim();
    if (!title || !form.rollout_strategy) return;
    const slug = isEdit ? form.slug : slugify(title);
    const payload = {
      slug,
      title,
      description: (form.description || "").trim(),
      rollout_strategy: form.rollout_strategy || "quick_win",
      rollout_model: form.rollout_model || "",
      transformation_theme: form.transformation_theme || "",
      complexity: form.complexity || "medium",
      recommended_use_case: (form.recommended_use_case || "").trim(),
      duration_min_weeks: toNumOrNull(form.duration_min_weeks),
      duration_max_weeks: toNumOrNull(form.duration_max_weeks),
      estimated_duration_weeks: toNumOrNull(form.estimated_duration_weeks),
      change_management_level: form.change_management_level || "",
      business_disruption_level: form.business_disruption_level || "",
      recommended_for_multi_site: !!form.recommended_for_multi_site,
      governance_required: !!form.governance_required,
      implementation_notes: (form.implementation_notes || "").trim(),
      rollout_groups: form.rollout_groups || [],
      phases_detail: (form.phases_detail || []).map((p) => ({
        name: p.name,
        duration_weeks: toNumOrNull(p.duration_weeks),
      })),
      resources: (form.resources || []).map((r) => ({
        role: r.role,
        typical_hours: toNumOrNull(r.typical_hours),
        onsite_rate_usd_per_hr: toNumOrNull(r.onsite_rate_usd_per_hr),
        offshore_rate_usd_per_hr: toNumOrNull(r.offshore_rate_usd_per_hr),
        allocation_pct: toNumOrNull(r.allocation_pct),
        notes: r.notes || "",
      })),
      weekly_effort_matrix: (form.weekly_effort_matrix || []).map((m) => ({
        phase: m.phase,
        role: m.role,
        hours_per_week: toNumOrNull(m.hours_per_week),
      })),
      active: !!form.active,
      sort_order: Number(form.sort_order) || 0,
    };
    setSaving(true);
    try {
      if (isEdit) await implementationTemplatesApi.update(form.id, payload);
      else await implementationTemplatesApi.create(payload);
      toast.success(
        `Implementation template ${isEdit ? "updated" : "created"}`
      );
      setDialog(false);
      await reload();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to save");
    } finally {
      setSaving(false);
    }
  };
  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.title}"?`)) return;
    try {
      await implementationTemplatesApi.remove(row.id);
      toast.success("Implementation template deleted");
      await reload();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="space-y-5" data-testid="si-implementation-templates">
      <header className="flex items-start justify-between">
        <div>
          <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#025979]">
            SmartImprove · Execution Programs
          </p>
          <h1
            className="text-2xl font-bold text-[#111827] mt-1"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            Implementation Templates
          </h1>
          <p className="text-sm text-[#6B7280] mt-1 max-w-3xl">
            Consulting-grade warehouse improvement execution templates used
            for rollout planning, transformation sequencing, resource
            estimation, and operational enhancement programs.
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs"
          data-testid="impl-add-btn"
        >
          <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Template
        </Button>
      </header>

      <div className="flex items-center gap-3">
        <label className="text-xs font-medium text-[#374151]">
          Filter by Strategy
        </label>
        <Select value={filterStrategy} onValueChange={setFilterStrategy}>
          <SelectTrigger
            className="rounded-sm text-xs w-72"
            data-testid="impl-filter-strategy"
          >
            <SelectValue placeholder="All" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">All Strategies</SelectItem>
            {ROLLOUT_STRATEGIES.map((s) => (
              <SelectItem key={s.value} value={s.value}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-xs text-[#6B7280] ml-auto">
          {sortedRows.length} template{sortedRows.length === 1 ? "" : "s"}
        </span>
      </div>

      {loading ? (
        <div className="px-5 py-8 text-sm text-[#6B7280]">Loading…</div>
      ) : sortedRows.length === 0 ? (
        <div
          className="bg-white border border-[#E5E7EB] rounded-sm px-5 py-12 text-center text-sm text-[#6B7280]"
          data-testid="impl-empty"
        >
          No implementation templates yet. Click <strong>Add Template</strong>{" "}
          to create the first one.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {sortedRows.map((row) => (
            <TemplateCard
              key={row.id}
              row={row}
              onEdit={openEdit}
              onRemove={remove}
            />
          ))}
        </div>
      )}

      <ImplementationDialog
        open={dialog}
        onOpenChange={setDialog}
        value={form}
        onChange={setForm}
        onSave={save}
        saving={saving}
      />
    </div>
  );
}
